import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { DataList, PageHeader, StatusPill, type DataListColumn, type StatusTone } from "@ozpanel/kit";
import { Button, Section, StateView, cn, useAuth } from "@ozpanel/ui";

// TunablesScreen — the ONE place to see every operational time-window in the platform: cooldowns,
// timeouts, poll/loop intervals, TTLs and self-fence deadlines. It answers, for each: what it gates,
// its compiled default, which module owns it, its safety class, and where in the code it lives — and
// gives a shortcut straight to the module (or platform screen) where it is changed.
//
// It reads the read-only catalog at GET /tunables (internal/tunables). It changes nothing itself;
// the safety class tells an operator which are free to tune, which are clamped to a derived floor,
// which are coupled safety GROUPS (edited together, invariant-validated), and which are read-only
// because a test pins them to a data-safety or security guarantee.

type Safety = "free" | "floor" | "grouped" | "locked" | "surfaced";

interface Tunable {
  key: string;
  label: string;
  home: string; // owning module key, or "platform" / "cluster"
  category: string;
  default: string;
  unit: string;
  safety: Safety;
  group?: string;
  gates: string;
  source: string;
  min?: string;
  wired: boolean; // the consumer reads its override → it is editable now, in its module's settings
}

const SAFETY: Record<Safety, { tone: StatusTone; label: string; hint: string }> = {
  free: { tone: "ok", label: "Free", hint: "Safe to tune — no correctness relationship." },
  floor: { tone: "accent", label: "Floor", hint: "Tunable, but clamped up to a derived minimum." },
  grouped: { tone: "warn", label: "Group", hint: "Edited as a coupled set; the group's invariant is validated on save." },
  locked: { tone: "bad", label: "Locked", hint: "Read-only — a test pins it to a data-safety or security guarantee." },
  surfaced: { tone: "neutral", label: "Live", hint: "Already an operator-editable setting today." },
};

// Where to change a tunable. A tunable homed to a real module deep-links to that module's Settings
// tab (ModuleDetailScreen reads ?tab=settings); a platform-level one goes to the Platform screen.
function editHref(t: Tunable): string {
  if (t.home === "platform") return "/platform";
  return `/modules/${t.home}?tab=settings`;
}

export function TunablesScreen() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tunables"],
    queryFn: () => client.apiFetch<{ items: Tunable[]; total: number }>("/tunables"),
  });
  const [filter, setFilter] = React.useState<Safety | "all" | "editable">("all");
  const [openKey, setOpenKey] = React.useState<string | null>(null);

  const all = q.data?.items ?? [];
  const counts = React.useMemo(() => {
    const c: Record<string, number> = {};
    for (const t of all) c[t.safety] = (c[t.safety] ?? 0) + 1;
    return c;
  }, [all]);
  const editableCount = all.filter((t) => t.wired).length;
  const items =
    filter === "all" ? all : filter === "editable" ? all.filter((t) => t.wired) : all.filter((t) => t.safety === filter);

  const columns: DataListColumn<Tunable>[] = [
    {
      key: "label",
      label: "Tunable",
      sortable: true,
      value: (t) => t.label,
      render: (t) => (
        <div>
          <div className="font-medium">{t.label}</div>
          <div className="font-mono text-xs text-muted">{t.key}</div>
        </div>
      ),
    },
    {
      key: "home",
      label: "Owner",
      sortable: true,
      value: (t) => t.home,
      render: (t) => <StatusPill tone="neutral">{t.home}</StatusPill>,
    },
    { key: "category", label: "Kind", sortable: true, value: (t) => t.category },
    {
      key: "default",
      label: "Default",
      sortable: true,
      value: (t) => t.default,
      render: (t) => <span className="font-mono">{t.default}</span>,
    },
    {
      key: "safety",
      label: "Adjust",
      sortable: true,
      value: (t) => t.safety,
      render: (t) => <StatusPill tone={SAFETY[t.safety].tone}>{SAFETY[t.safety].label}</StatusPill>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Tunables"
        subtitle="Every cooldown, timeout, interval, TTL and self-fence deadline — what it gates, its default, who owns it, and where to change it."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
          All {all.length}
        </FilterChip>
        <FilterChip active={filter === "editable"} onClick={() => setFilter("editable")} title="Wired to their module settings — editable now.">
          Editable now {editableCount}
        </FilterChip>
        {(Object.keys(SAFETY) as Safety[]).map((s) => (
          <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)} title={SAFETY[s].hint}>
            {SAFETY[s].label} {counts[s] ?? 0}
          </FilterChip>
        ))}
      </div>

      <StateView loading={q.isLoading} error={q.error} errorContext="tunables">
        <DataList
          items={items}
          columns={columns}
          getRowKey={(t) => t.key}
          searchable
          searchPlaceholder="Search name, owner, kind…"
          onRowClick={(t) => setOpenKey((k) => (k === t.key ? null : t.key))}
          expandedKey={openKey}
          renderExpanded={(t) => (
            <div className="space-y-3 p-3 text-sm">
              <p>{t.gates}</p>
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs text-muted">
                <span>
                  <span className="font-medium text-fg">Adjust:</span> {SAFETY[t.safety].hint}
                </span>
                {t.group && (
                  <span>
                    <span className="font-medium text-fg">Group:</span> <span className="font-mono">{t.group}</span>{" "}
                    (edited together, invariant-validated)
                  </span>
                )}
                <span>
                  <span className="font-medium text-fg">Source:</span> <span className="font-mono">{t.source}</span>
                </span>
              </div>
              <div>
                {t.wired ? (
                  <Link to={editHref(t)}>
                    <Button variant="soft" size="sm">
                      Change in {t.home === "platform" ? "Platform" : t.home} settings → Timing &amp; cooldowns →
                    </Button>
                  </Link>
                ) : (
                  <span className="text-xs text-muted">
                    {t.safety === "locked" || t.safety === "grouped"
                      ? "Read-only — changed only in code (see Source)."
                      : "Not wired to a settings field yet — currently read from code (see Source)."}
                  </span>
                )}
              </div>
            </div>
          )}
          rowActions={(t) =>
            t.wired ? (
              <Link
                to={editHref(t)}
                className="text-sm text-accent hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Change →
              </Link>
            ) : (
              <span className="text-xs text-muted">code</span>
            )
          }
        />
      </StateView>

      <Section title="What the safety classes mean" className="mt-6">
        <ul className="space-y-1 p-4 text-sm text-muted">
          {(Object.keys(SAFETY) as Safety[]).map((s) => (
            <li key={s}>
              <StatusPill tone={SAFETY[s].tone}>{SAFETY[s].label}</StatusPill> <span className="ml-2">{SAFETY[s].hint}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "rounded-full border px-3 py-1 text-xs",
        active ? "border-accent bg-accent/10 text-accent" : "border-line text-muted hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}
